import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTable, useSortBy } from 'react-table';
import cn from 'classnames';
import { useOutletContext } from 'react-router-dom';
import styles from './checklist.module.scss';
import TileView from '../TileView/index';
import { getModes } from './Mode.js';
import Counters from './Counters';
import { FLABEL } from '@containers/Fireboard/fireboardVariables';
import { safeJsonParse } from '../../../../../utility/helperFunction';

const ChecksList = props => {
  const {
    checksRowData,
    setSelectedCheck,
    selectedCheck,
    currentView,
    checkFilters,
    currentMode,
    setIsFlip,
    futureOrdersSummary,
  } = props;
  const tableWrapperRef = useRef(null);
  const flabel = FLABEL();
  const [checksCount, setChecksCount] = useState();
  const { setSortedCheckList } = useOutletContext();

  const deviceGroup = safeJsonParse(window.sessionStorage.getItem('deviceInfo'), {})
    ?.deviceGroup;
  const isTableGrouping =
    deviceGroup?.fireBoardSettings?.groupedView && !futureOrdersSummary;
  const filters = deviceGroup?.fireBoardCheckFilters;
  const sourceTypeMap = useMemo(
    () => ({
      0: FLABEL().CHANNEL_POS,
      1: FLABEL().CHANNEL_KIOSK,
      3: FLABEL().CHANNEL_ORDER_PAY,
      5: FLABEL().CHANNEL_CURBSIDE,
      6: FLABEL().CHANNEL_ONLINE,
      7: FLABEL().CHANNEL_WALKUP,
      8: FLABEL().CHANNEL_CALL_IN,
      9: FLABEL().CHANNEL_COUNTER,
      10: FLABEL().CHANNEL_CALL_CENTER,
      11: FLABEL().CHANNEL_SPEED_ORDER,
      12: FLABEL().CHANNEL_DRIVE_THRU,
    }),
    [flabel]
  );

  const sourceTypes = useMemo(() => {
    // If channel filters exist and have values show only those
    if (
      deviceGroup?.fireBoardCheckFilterType === 0 &&
      Array.isArray(filters) &&
      filters.length > 0
    ) {
      return filters
        .map(type => String(type))
        .filter(type => sourceTypeMap[type] !== undefined);
    }
    return Object.keys(sourceTypeMap);
  }, [sourceTypeMap]);

  const onRowClicked = (data, rowIndex) => {
    setSelectedCheck(data);
  };
  const groupedSourceTypeData = useMemo(() => {
    if (!isTableGrouping) return checksRowData;
    return sourceTypes.reduce((acc, type) => {
      const groupItems = checksRowData.filter(
        item => item.sourceType === Number(type)
      );
      if (groupItems.length > 0) {
        acc.push(
          {
            sourceType: sourceTypeMap[type],
            isGroupHeader: true,
            length: groupItems.length || 0,
          },
          ...groupItems
        );
      } else {
        acc.push(
          { sourceType: sourceTypeMap[type], isGroupHeader: true, length: 0 },
          {
            isPlaceholder: true,
            sourceType: sourceTypeMap[type],
            status: 'No Record Found',
          }
        );
      }

      return acc;
    }, []);
  }, [checksRowData, isTableGrouping, sourceTypes]);
  const modes = useMemo(() => getModes(), []);

  const columns = useMemo(() => modes[currentMode] || modes.DEFAULT, [
    modes,
    currentMode,
  ]);
  const tableInstance = useTable(
    {
      columns,
      data: groupedSourceTypeData,
      initialState: !isTableGrouping && {
        sortBy: [
          {
            id: 'number',
            desc: false,
          },
        ],
      },
      disableSortBy: isTableGrouping,
    },
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { sortBy },
  } = tableInstance;
  const getFirstSelectedRow = () => {
    if (isTableGrouping) {
      const firstNonPlaceholderRow = rows.find(
        row => !row.original.isGroupHeader && !row.original.isPlaceholder
      );
      return firstNonPlaceholderRow?.original || null;
    } else {
      return checksRowData[0] || null;
    }
  };
  useEffect(() => {
    const checkSelectedInFilter =
      checksRowData.find(ele => ele.id === selectedCheck) || null;
    if (selectedCheck === null || !checkSelectedInFilter) {
      const initialRow = getFirstSelectedRow();
      if (initialRow) {
        setSelectedCheck(initialRow?.id);
      }
    }
  }, [checksRowData, selectedCheck === null]);

  useEffect(() => {
    if (tableWrapperRef.current) {
      tableWrapperRef.current.scrollTo({
        top: 0,
        behavior: 'smooth', //'instant' if user want immediate change
      });
    }
  }, [currentMode]);

  // checking is sort is enabled used for handleNextCheck and handlePrevCheck in expanded view
  useEffect(() => {
    const sortedIds = rows
      ?.filter(row => !row?.original?.isGroupHeader && !row?.original?.isPlaceholder)
      ?.map(row => row?.original?.id) || [];
    setSortedCheckList(sortedIds);
  }, [rows, setSortedCheckList]);


  const ChecksTable = () => {
    return (
      <table className={styles.tablestyle} {...getTableProps()}>
        <thead className={styles.fixedHeader}>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  style={{
                    padding: '8px 12px 5px 12px',
                    textAlign: 'center',
                    fontWeight: '500',
                    cursor: column.canSort ? 'pointer' : 'default',
                  }}
                >
                  {/* columns with headerParts given to sort the column with two headers*/}
                  {column.headerParts ? (
                    <div className={styles.headerContent}>
                      {column.headerParts.map(part => (
                        <div>
                          <span>{part.label}</span>
                          <span className="pl-1">
                            {part.sortable && column.isSorted
                              ? column.isSortedDesc
                                ? '▼'
                                : '▲'
                              : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      {column.render('Header')}
                      {column.isSorted ? (
                        <span className="pl-1">
                          {column.isSortedDesc ? '▼' : '▲'}
                        </span>
                      ) : (
                        ''
                      )}
                    </>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody
          {...getTableBodyProps()}
          style={{ color: 'var(--table-body-text)' }}
        >
          {rows.length > 0 ? (
            rows.map((row, index) => {
              prepareRow(row);
              if (row.original.isGroupHeader) {
                return (
                  <tr
                    {...row.getRowProps()}
                    style={{
                      background: 'var(--group-view-bar)',
                      height: '18px',
                    }}
                    className={styles.sourceTypeHeader}
                  >
                    <td colSpan={columns.length}>
                      {row.original.sourceType} {`(${row.original.length})`}
                    </td>
                  </tr>
                );
              }
              if (row.original.isPlaceholder) {
                return (
                  <tr {...row.getRowProps()}>
                    <td colSpan={columns.length} className="text-center">
                      {row.original.status}
                    </td>
                  </tr>
                );
              }
              return (
                <tr
                  {...row.getRowProps()}
                  className={cn(
                    isTableGrouping && styles.rowHeightForItem,
                    selectedCheck === row?.original?.id && styles.selecteditem
                  )}
                  style={{
                    backgroundColor:
                      index % 2 === 0
                        ? 'var(--table-row-bg2)'
                        : 'var(--table-row-bg1)',
                  }}
                >
                  {row.cells.map(cell => (
                    <td
                      {...cell.getCellProps()}
                      style={{
                        padding: isTableGrouping ? '8px' : '7px',
                        textAlign: 'center',
                      }}
                      onClick={() => {
                        onRowClicked(row?.original?.id);
                        setIsFlip(false);
                      }}
                    >
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              );
            })
          ) : (
            <tr>
              <td
                colSpan={columns.length}
                style={{ padding: 0, border: 'none' }}
              >
                <div className={styles.noRecordStyle}>
                  {FLABEL().CHECK_NO_CHECKS_AVAILABLE}
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    );
  };

  return (
    <>
      <div
        ref={tableWrapperRef}
        className={cn(styles.tableWrapper, 'col-12 px-0')}
      >
        {futureOrdersSummary || currentView === 'listView' ? (
          <ChecksTable />
        ) : (
          <TileView
            checks={checksRowData}
            selectedCheck={selectedCheck}
            checksCount={checksCount}
            setSelectedCheck={setSelectedCheck}
            checkFilters={checkFilters}
            currentMode={currentMode}
          />
        )}
      </div>
      <Counters
        checksCount={checksCount}
        checksRowData={checksRowData}
        setChecksCount={setChecksCount}
        currentMode={currentMode}
        filteredChecksList={props.filteredChecksList}
        setFilteredCheckListItem={props.setFilteredCheckListItem}
        filterByOrderTypes={props.filterByOrderTypes}
      />
    </>
  );
};
export default ChecksList;
